// ============================================================================
// addressParse.js — SMART Build 2N (pure; Gate address parsing)
//
// Splits a flattened address string ("456 Maple Avenue, Austin, TX 78701") into
// { street, city, state, zip } for the Gate patient-address fields, and rebuilds
// the canonical joined form. Extracted from PatientStepPHI so it is unit-testable
// and shared by EHR auto-fill + OCR prefill.
//
// Conservative by design: if it can't confidently split a segment it leaves it in
// `street` (or blank) rather than guessing. It NEVER places a country token into
// the city — a trailing country segment ("…, 01772, US") is stripped first. State
// is normalized to the US postal code the State <select> expects.
//
// Pure: no Firestore, no network, no logging, no PHI persisted anywhere.
// ============================================================================

// Canonical US state options (value = postal code) — also the State <select> list.
export const US_STATES = [
  { value: '',   label: 'State' },
  { value: 'AL', label: 'AL — Alabama' },
  { value: 'AK', label: 'AK — Alaska' },
  { value: 'AZ', label: 'AZ — Arizona' },
  { value: 'AR', label: 'AR — Arkansas' },
  { value: 'CA', label: 'CA — California' },
  { value: 'CO', label: 'CO — Colorado' },
  { value: 'CT', label: 'CT — Connecticut' },
  { value: 'DE', label: 'DE — Delaware' },
  { value: 'FL', label: 'FL — Florida' },
  { value: 'GA', label: 'GA — Georgia' },
  { value: 'HI', label: 'HI — Hawaii' },
  { value: 'ID', label: 'ID — Idaho' },
  { value: 'IL', label: 'IL — Illinois' },
  { value: 'IN', label: 'IN — Indiana' },
  { value: 'IA', label: 'IA — Iowa' },
  { value: 'KS', label: 'KS — Kansas' },
  { value: 'KY', label: 'KY — Kentucky' },
  { value: 'LA', label: 'LA — Louisiana' },
  { value: 'ME', label: 'ME — Maine' },
  { value: 'MD', label: 'MD — Maryland' },
  { value: 'MA', label: 'MA — Massachusetts' },
  { value: 'MI', label: 'MI — Michigan' },
  { value: 'MN', label: 'MN — Minnesota' },
  { value: 'MS', label: 'MS — Mississippi' },
  { value: 'MO', label: 'MO — Missouri' },
  { value: 'MT', label: 'MT — Montana' },
  { value: 'NE', label: 'NE — Nebraska' },
  { value: 'NV', label: 'NV — Nevada' },
  { value: 'NH', label: 'NH — New Hampshire' },
  { value: 'NJ', label: 'NJ — New Jersey' },
  { value: 'NM', label: 'NM — New Mexico' },
  { value: 'NY', label: 'NY — New York' },
  { value: 'NC', label: 'NC — North Carolina' },
  { value: 'ND', label: 'ND — North Dakota' },
  { value: 'OH', label: 'OH — Ohio' },
  { value: 'OK', label: 'OK — Oklahoma' },
  { value: 'OR', label: 'OR — Oregon' },
  { value: 'PA', label: 'PA — Pennsylvania' },
  { value: 'RI', label: 'RI — Rhode Island' },
  { value: 'SC', label: 'SC — South Carolina' },
  { value: 'SD', label: 'SD — South Dakota' },
  { value: 'TN', label: 'TN — Tennessee' },
  { value: 'TX', label: 'TX — Texas' },
  { value: 'UT', label: 'UT — Utah' },
  { value: 'VT', label: 'VT — Vermont' },
  { value: 'VA', label: 'VA — Virginia' },
  { value: 'WA', label: 'WA — Washington' },
  { value: 'WV', label: 'WV — West Virginia' },
  { value: 'WI', label: 'WI — Wisconsin' },
  { value: 'WY', label: 'WY — Wyoming' },
  { value: 'DC', label: 'DC — District of Columbia' },
  { value: 'PR', label: 'PR — Puerto Rico' },
  { value: 'GU', label: 'GU — Guam' },
  { value: 'VI', label: 'VI — U.S. Virgin Islands' },
];

const US_STATE_CODE_SET = new Set(US_STATES.map((s) => s.value).filter(Boolean));
const US_STATE_NAME_TO_CODE = US_STATES
  .filter((s) => s.value && s.label.includes('—'))
  .map((s) => [s.label.split('—')[1].trim(), s.value]);

// Recognized trailing country tokens — stripped so country never becomes city.
function isCountryToken(seg) {
  const s = String(seg || '').trim().replace(/\./g, '').replace(/\s+/g, ' ').toUpperCase();
  return s === 'US' || s === 'USA' || s === 'UNITED STATES' || s === 'UNITED STATES OF AMERICA' || s === 'AMERICA';
}

/**
 * Normalize a state value to the US postal code the State <select> expects.
 * "Massachusetts" → "MA"; "ma" → "MA"; unknown values are preserved as-is.
 */
export function normalizeStateToCode(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  if (US_STATE_CODE_SET.has(v.toUpperCase())) return v.toUpperCase();
  for (const [name, code] of US_STATE_NAME_TO_CODE) {
    if (name.toLowerCase() === v.toLowerCase()) return code;
  }
  return v; // unknown — preserve (do not guess)
}

/**
 * Split a flattened address into { street, city, state, zip }. Best-effort and
 * conservative: a trailing country segment is dropped first; ZIP (5 or ZIP+4) is
 * read from the end; state is a 2-letter code or full name normalized to a code;
 * the remainder splits into street/city. Unconfident parts stay in street.
 */
export function parseAddress(raw) {
  let rest = String(raw || '').trim().replace(/\s+/g, ' ');
  if (!rest) return { street: '', city: '', state: '', zip: '' };

  // Strip a trailing country segment ("…, 01772, US") — never let it reach city.
  // Only when there are >= 2 comma segments (so a single-token value is untouched).
  {
    const parts = rest.split(',').map((s) => s.trim());
    if (parts.length >= 2 && isCountryToken(parts[parts.length - 1])) {
      parts.pop();
      rest = parts.join(', ').replace(/[,\s]+$/, '');
    }
  }

  let zip = '';
  let state = '';
  // ZIP (5 or ZIP+4) at the end.
  const zipM = rest.match(/\b(\d{5}(?:-\d{4})?)\s*$/);
  if (zipM) { zip = zipM[1]; rest = rest.slice(0, zipM.index).trim().replace(/[,\s]+$/, ''); }
  // State: 2-letter code, else full name.
  const codeM = rest.match(/[,\s]([A-Za-z]{2})\s*$/);
  if (codeM && US_STATE_CODE_SET.has(codeM[1].toUpperCase())) {
    state = codeM[1].toUpperCase();
    rest = rest.slice(0, codeM.index).trim().replace(/[,\s]+$/, '');
  } else {
    for (const [name, code] of US_STATE_NAME_TO_CODE) {
      const re = new RegExp('[,\\s]' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i');
      const m = rest.match(re);
      if (m) { state = code; rest = rest.slice(0, m.index).trim().replace(/[,\s]+$/, ''); break; }
    }
  }
  // Remaining = "street, city" (preferred) or "street … city".
  let street = rest;
  let city = '';
  const commaParts = rest.split(',').map((s) => s.trim()).filter(Boolean);
  if (commaParts.length >= 2) { city = commaParts.pop(); street = commaParts.join(', '); }
  else if (state || zip) {
    const tokens = rest.split(' ').filter(Boolean);
    if (tokens.length >= 2) { city = tokens.pop(); street = tokens.join(' '); }
  }
  return { street: street.trim(), city: city.trim(), state, zip };
}

/**
 * Canonical joined form — the pending baseline + reconstructed value so "unchanged"
 * comparisons (ACCEPT vs EDIT, clear-if-unchanged) stay stable.
 */
export const canonicalAddress = ({ street, city, state, zip }) =>
  [street, city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

export default { US_STATES, parseAddress, canonicalAddress, normalizeStateToCode };
